<template>
    <div>
        <!--
            The user has selected at least 1 size, language and group value
            but has no deliverables due to the QA status and/or published status
        -->
        <hox-empty-message v-if="isFilteringByMetadataSearch">
            <template #title>No deliverables with the selected filters and column search terms could be found</template>
        </hox-empty-message>
        <!--
            The user has selected at least 1 size, language and group value
            but has no deliverables due to the QA status and/or published status
        -->
        <hox-empty-message v-else-if="isFilteringByStatus && isFilteringByPublishedStatus">
            <template #title>
                No deliverables with the selected languages, sizes and group values have the QA status "
                <em>{{ statusFilterLabel }}</em>
                " and the published status "
                <em>{{ publishedStatusFilterLabel }}</em>
                "
            </template>
        </hox-empty-message>
        <!--
            The user has selected at least 1 size, language and group value
            but has no deliverables due to the published status.
        -->
        <hox-empty-message v-else-if="isFilteringByPublishedStatus">
            <template #title>
                No deliverables with the selected languages, sizes and group values have the published status "
                <em>{{ publishedStatusFilterLabel }}</em>
                "
            </template>
        </hox-empty-message>
        <!--
            The user has selected at least 1 size, language and group value
            but has no deliverables due to the visibleTo Filter.
        -->
        <hox-empty-message v-else-if="isFilteringByVisibleTo">
            <template #title>
                No deliverables with the selected languages, sizes and group values are visible to selected role
            </template>
        </hox-empty-message>
        <!--
            The user has selected at least 1 size, language and group value
            but has no deliverables due to the QA status.
        -->
        <hox-empty-message v-else-if="isFilteringByStatus">
            <template #title>
                No deliverables with the selected languages, sizes and group values have the QA status "
                <em>{{ statusFilterLabel }}</em>
                "
            </template>
        </hox-empty-message>

        <hox-empty-message v-else-if="isFilteringByCommentedOnBy">
            <template #title>
                No deliverables with the selected languages, sizes and group values that have "
                <em>{{ commentedOnByFilterLabel }}</em>
                " comments
            </template>
        </hox-empty-message>

        <hox-empty-message v-else-if="isFilteringByUserStatus">
            <template #title>
                No deliverables with the selected languages, sizes and group values that match your selection in Review
                Status filter
            </template>
        </hox-empty-message>

        <!--
            The user has selected at least 1 size, language and group value but
            can not view any deliverables, most likely due to their role.
        -->
        <hox-empty-message v-else>
            <template #title>There are no deliverables with the selected languages, sizes and group values</template>
        </hox-empty-message>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";

import {
    BannerQaCommentedOnByFilter,
    BannerQaCommentedOnByFilterLabel,
    BannerQaStatusFilter,
    BannerQaStatusFilterLabel,
    BannerQaUserStatusFilter,
    PublishedStatusFilter
} from "@/enums/filters";
import { useCreativeLibraryFiltersStore } from "@/store/pinia/creativeLibraryFilters";
import { PlatformLabel } from "../../enums/platforms";
import { PLATFORM } from "shared-utils/enums/exports";

const PublishedStatusFilterLabel = {
    [PublishedStatusFilter.All]: "All",
    [PublishedStatusFilter.NotPublished]: "Not published",
    [PublishedStatusFilter.NotPublishedToDoubleClick]: `Not published on ${PlatformLabel[PLATFORM.DOUBLECLICK]}`,
    [PublishedStatusFilter.NotPublishedToFlashtalking]: `Not published on ${PlatformLabel[PLATFORM.FLASHTALKING]}`,
    [PublishedStatusFilter.NotPublishedToOpenDC]: `Not published on ${PlatformLabel[PLATFORM.OPENDC]}`,
    [PublishedStatusFilter.Published]: "Published",
    [PublishedStatusFilter.PublishedToDoubleClick]: `Published on ${PlatformLabel[PLATFORM.DOUBLECLICK]}`,
    [PublishedStatusFilter.PublishedToFlashtalking]: `Published on ${PlatformLabel[PLATFORM.FLASHTALKING]}`,
    [PublishedStatusFilter.PublishedToOpenDC]: `Published on ${PlatformLabel[PLATFORM.OPENDC]}`
};

const creativeLibraryFiltersStore = useCreativeLibraryFiltersStore();

const isFilteringByMetadataSearch = computed(() => Object.keys(creativeLibraryFiltersStore.metadataSearch).length > 0);
const isFilteringByStatus = computed(() => creativeLibraryFiltersStore.qaStatus !== BannerQaStatusFilter.All);
const isFilteringByVisibleTo = computed(() => creativeLibraryFiltersStore.visibleTo !== "Me");

const isFilteringByPublishedStatus = computed(
    () => creativeLibraryFiltersStore.publishedStatus !== PublishedStatusFilter.All
);

const isFilteringByCommentedOnBy = computed(
    () => creativeLibraryFiltersStore.commentedOnBy !== BannerQaCommentedOnByFilter.All
);

const isFilteringByUserStatus = computed(
    () => !creativeLibraryFiltersStore.reviewStatus.includes(BannerQaUserStatusFilter.All)
);

const statusFilterLabel = computed(() => BannerQaStatusFilterLabel[creativeLibraryFiltersStore.qaStatus]);
const publishedStatusFilterLabel = computed(
    () => PublishedStatusFilterLabel[creativeLibraryFiltersStore.publishedStatus]
);

const commentedOnByFilterLabel = computed(
    () => BannerQaCommentedOnByFilterLabel[creativeLibraryFiltersStore.commentedOnBy]
);
</script>
